import { CircularProgress, Fade, styled } from '@mui/material';
import { forwardRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface LoadingPaneProps {
  backgroundColor?: string;
  children?: ReactNode;
  className?: string;
  delay?: string;
  in: boolean;
  size?: string | number;
  thickness?: number;
}

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  height: '100%',
  padding: 0,
  position: 'relative',
});

const Overlay = styled('div', { shouldForwardProp: (prop) => prop !== 'background' })<{
  background?: string;
}>(({ background }) => ({
  backgroundColor: background ?? 'rgba(255, 255, 255, 0.5)',
  display: 'flex',
  height: '100%',
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'absolute',
  top: 0,
  left: 0,
}));

export const LoadingPane = forwardRef<HTMLDivElement, LoadingPaneProps>(
  function LoadingPane(props, ref) {
    const { backgroundColor, children, className, delay, size, thickness } = props;
    const { t } = useTranslation();

    return (
      <Root aria-busy={props.in} ref={ref} className={className}>
        {children}
        <Fade
          in={props.in}
          style={{ transitionDelay: props.in ? delay ?? '800ms' : '0ms' }}
          unmountOnExit
        >
          <Overlay background={backgroundColor} role="status">
            <CircularProgress
              aria-label={t('common.status.loading')}
              size={size}
              thickness={thickness}
            />
          </Overlay>
        </Fade>
      </Root>
    );
  },
);
